import { Building2, UserPlus } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";

export type CreateEntityKind = "organization" | "client";

export interface CreateEntityChoiceProps {
  /** Fired when the user picks a card; advances the wizard immediately (mock `choice` screen behaviour). */
  onSelect: (entity: CreateEntityKind) => void;
}

interface ChoiceCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}

function ChoiceCard({ icon: Icon, title, description, onClick }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-primary"
    >
      <span className="flex size-10 items-center justify-center rounded-lg bg-muted text-foreground">
        <Icon size={20} strokeWidth={1.9} />
      </span>
      <span>
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="mt-1 block text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

/**
 * Entity selection screen of the unified create flow (internal users only): Organization vs Client cards.
 * Clicking a card advances directly — there is no Continue button on this screen in the design.
 */
export function CreateEntityChoice({ onSelect }: CreateEntityChoiceProps) {
  const { t } = useTranslations("clients");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("choice_entity_description")}</p>
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <ChoiceCard
          icon={Building2}
          title={t("choice_card_organization_title")}
          description={t("choice_card_organization_description")}
          onClick={() => onSelect("organization")}
        />
        <ChoiceCard
          icon={UserPlus}
          title={t("choice_card_client_title")}
          description={t("choice_card_client_description")}
          onClick={() => onSelect("client")}
        />
      </div>
    </div>
  );
}
